import { getI18nLocale } from "@/plugins/i18n";
import store from "@/store";
import { retryCompleteRequest } from "./complete-retry";
import { retryOnLockConflict } from "./lock-retry";
import { OrderLockException } from "./trainplanet.exceptions";

const axios = require("axios");

export default class TrainplanetApi {
  constructor(orderId, tokenParameter) {
    const token = tokenParameter.split("&")[0];

    store.commit(
      "order/setOfferMode",
      tokenParameter.split("offerMode=")[1] === "true"
    );

    this.orderId = orderId;
    this.loginToken = token;
    this.twoAuthHeaderIsRequired = false;

    // TrainPlanet API instance.
    this.client = axios.create({
      baseURL: `${process.env.VUE_APP_TRAINPLANET_API_URL_BASE}/orders/self-service`,
    });

    // Request middleware
    this.client.interceptors.request.use((request) => {
      request.headers["Accept-Language"] = getI18nLocale();
      request.headers["Content-Type"] = "application/json";

      if (!request.skipAuthHeader) {
        request.headers["AuthorizationSelfService"] = this.loginToken;
      }

      let token = localStorage.getItem("token");
      if (!token && this.twoAuthHeaderIsRequired) {
        // TODO force customer to log in again if the token is mandatory but does not exist
        // this.login({token: this.loginToken})
        // token = localStorage.getItem("token");
      }

      return request;
    });
  }

  generateToken() {
    this.twoAuthHeaderIsRequired = false;
    const promise = this.client.post(
      `/${this.orderId}/generate-token`,
      {
        email: this.loginToken,
      },
      { skipAuthHeader: true }
    );
    return promise;
  }

  login(data) {
    this.twoAuthHeaderIsRequired = false;
    return this.client.post(`/${this.orderId}/login`, data).then(
      (response) => {
        try {
          const decoded = atob(response.data.token);
          localStorage.setItem("orderId", decoded.split(":")[0]);
        } catch (e) {
          console.warn("Token could not be base64 decoded:", e);
        }
        localStorage.setItem("password", response.data.token);
        localStorage.setItem("token", response.data.token);
        return response;
      },
      { skipAuthHeader: true }
    );
  }

  async getOrder(orderId) {
    this.twoAuthHeaderIsRequired = true;
    const { data } = await this.client.get(`/${orderId}`);
    return data;
  }

  async getOrderInvoice(orderId) {
    this.twoAuthHeaderIsRequired = true;
    return this.client.get(`/${orderId}`);
  }

  // Takes the same backend lock as /payment/session/../complete, so it can lose
  // the race against a complete that is running in another tab or was triggered
  // by Swish's server-side callback. Retried rather than guarded, because the
  // lock holder is not visible from here (TRAIN-2238).
  async listDistributionOptions(orderId) {
    const { data } = await retryOnLockConflict(() => {
      this.twoAuthHeaderIsRequired = true;
      return this.client.get(`/${orderId}/distributions`);
    });
    return data;
  }

  async removeItem(orderId, itemId) {
    this.twoAuthHeaderIsRequired = true;
    const { data } = await this.client.delete(
      `/${orderId}/item?itemIds[]=${itemId}`
    );
    return data;
  }

  async updateOrder(orderId, payload) {
    this.twoAuthHeaderIsRequired = true;
    const { data } = await this.client.patch(`/${orderId}`, payload);
    return data;
  }

  async cancelAndRefund(payload) {
    this.twoAuthHeaderIsRequired = true;
    const { data } = await this.client.post(`/${this.orderId}/refund`, payload);
    return data;
  }

  async cancelOrder(payload) {
    this.twoAuthHeaderIsRequired = true;
    const { data } = await this.client.post(`/${this.orderId}/cancel`, payload);
    return data;
  }

  async getRefundAmount(payload) {
    this.twoAuthHeaderIsRequired = true;
    const { data } = await this.client.post(
      `/${this.orderId}/refund-amount`,
      payload
    );
    return data;
  }

  async resendTicket() {
    this.twoAuthHeaderIsRequired = true;
    const { data } = await this.client.post(`/${this.orderId}/send-tickets`);
    return data;
  }

  async getFiles() {
    this.twoAuthHeaderIsRequired = true;
    const { data } = await this.client.post(
      `/${this.orderId}/send-tickets?getAsPdfLinks=true`
    );
    return data;
  }

  async getDistributions(payload) {
    const { data } = await this.client.get(`/${this.orderId}/distributions`, {
      params: {
        ...payload,
      },
    });
    return data;
  }

  async validateTicket() {
    this.twoAuthHeaderIsRequired = true;
    const { data } = await this.client.post(
      `/${this.orderId}/validate-ticket/`
    );
    return data;
  }

  async lockOrder(orderId, reason = "") {
    this.twoAuthHeaderIsRequired = true;
    try {
      const { data } = await this.client.post(`/${orderId}/lock`, {
        reason,
      });
      return data;
    } catch (error) {
      throw new OrderLockException(error.response);
    }
  }

  async unlockOrder(orderId) {
    this.twoAuthHeaderIsRequired = true;
    const { data } = await this.client.post(`/${orderId}/unlock`);
    return data;
  }

  async getPaymentSession(
    system,
    orderId,
    checkoutUrl,
    confirmationUrl,
    payload = {}
  ) {
    this.twoAuthHeaderIsRequired = true;

    return await this.client.post(
      `/${orderId}/payment/session/${system}`,
      payload,
      {
        params: {
          checkoutUrl,
          confirmationUrl,
        },
      }
    );
  }

  async validatePayment(orderId) {
    this.twoAuthHeaderIsRequired = true;
    return await this.client.post(`/${orderId}/validate-payment`);
  }

  async addGiftcards(orderId, payload) {
    this.twoAuthHeaderIsRequired = true;
    return await this.client.post(`/${orderId}/add-giftcard`, payload);
  }

  async confirmationCheck(pushUrl, accountId, selectedShippingSlug) {
    this.twoAuthHeaderIsRequired = true;
    return await retryCompleteRequest(() =>
      this.client.post(
        pushUrl,
        { accountId, selectedShippingSlug },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      )
    );
  }

  static async getTenant(domain = window.location.hostname) {
    const { data } = await axios.get(
      `${process.env.VUE_APP_TRAINPLANET_API_URL_BASE}/tenants`,
      { params: { domain } }
    );
    return data[0];
  }

  static async getTenants() {
    const { data } = await axios.get(
      `${process.env.VUE_APP_TRAINPLANET_API_URL_BASE}/tenants`
    );
    return data;
  }

  async completePaymentSession(
    orderId,
    system,
    paymentId,
    selectedShippingSlug = "email",
    retryOptions = {}
  ) {
    this.twoAuthHeaderIsRequired = true;
    const { data } = await retryCompleteRequest(
      () =>
        this.client.post(
          `/${orderId}/payment/session/${system}/callback/${paymentId}/complete`,
          { selectedShippingSlug },
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        ),
      retryOptions
    );
    return data;
  }

  async checkPaymentStatus(orderId, system, paymentId) {
    this.twoAuthHeaderIsRequired = true;
    const { data } = await this.client.get(
      `/${orderId}/payment/session/${system}/callback/${paymentId}/status`
    );
    return data;
  }

  async getSystemFortnox(orderId, checkoutURL) {
    this.twoAuthHeaderIsRequired = true;
    const { data } = await this.client.get(`/${orderId}/payment/fortnox`);
    return data;
  }

  async getTaxiOptions(orderId, payload) {
    this.twoAuthHeaderIsRequired = true;
    const { data } = await this.client.post(
      `/${orderId}/taxi-options`,
      payload
    );
    return data;
  }

  async getTaxiPayload(orderId) {
    this.twoAuthHeaderIsRequired = true;
    const { data } = await this.client.get(`/${orderId}/taxi-payload`);
    return data;
  }

  async addItemToOrder(orderId, payload) {
    this.twoAuthHeaderIsRequired = true;
    const { data } = await this.client.post(`/${orderId}/item`, payload);
    return data;
  }
}
